import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, BookOpen, Trophy, BarChart3, Sparkles, Plus } from "lucide-react";
import api from "@/lib/api";
import TopNav from "@/components/TopNav";

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <div className="tw-card p-6 tw-card-hover" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-').replace(/\./g,'')}`}>
      <div className="flex items-center justify-between">
        <div className="tw-mono-label">{label}</div>
        {Icon && <Icon size={16} className="text-stone-400"/>}
      </div>
      <div className="tw-display text-4xl font-extrabold mt-3 tracking-tighter">{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </div>
  );
}

function CourseCard({ c }) {
  const isStarted = c.completed_lessons > 0;
  const status = c.progress_pct === 100 ? "complete" : isStarted ? "in_progress" : "available";
  const pill = status === "complete" ? "tw-pill-done" : status === "in_progress" ? "tw-pill-prog" : "tw-pill-lock";
  const fillBg = status === "complete" ? "var(--tawbti-green-fill)" : "var(--tawbti-blue-fill)";
  return (
    <Link to={`/courses/${c.course_id}`} className="tw-card p-6 tw-card-hover block" data-testid={`my-course-${c.course_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="h-2 w-12 rounded-full" style={{background:c.cover_color || "#185FA5"}}/>
        <span className={`tw-pill ${pill}`}>{status === "complete" ? "Complete" : status === "in_progress" ? "In progress" : "Not started"}</span>
      </div>
      <div className="tw-mono-label mt-4">{c.course_id} · {c.category}</div>
      <h3 className="tw-display font-bold text-xl mt-2 leading-tight">{c.title}</h3>
      <p className="text-stone-600 text-sm mt-2 line-clamp-2 leading-relaxed">{c.subtitle || c.description}</p>
      <div className="mt-5">
        <div className="flex items-center justify-between text-xs text-stone-500 mb-1.5"><span>{c.completed_lessons} / {c.total_lessons} lessons</span><span className="font-bold text-stone-800">{c.progress_pct}%</span></div>
        <div className="tw-progress-track"><div className="tw-progress-fill" style={{width:`${c.progress_pct}%`, background: fillBg}}/></div>
      </div>
      <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold">Continue <ArrowRight size={14}/></div>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [myCourses, setMyCourses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [m, s] = await Promise.all([api.get("/my/courses"), api.get("/dashboard/stats")]);
        setMyCourses(m.data);
        setStats(s.data);
      } finally { setLoaded(true); }
    })();
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "Learner";
  const active = myCourses.find(c => c.progress_pct > 0 && c.progress_pct < 100) || myCourses[0];

  if (!loaded) return <div className="min-h-screen"><TopNav/><div className="max-w-7xl mx-auto px-6 py-16 text-stone-500" data-testid="dashboard-loading">Loading your learning…</div></div>;

  return (
    <div className="min-h-screen pb-24">
      <TopNav />
      <div className="max-w-7xl mx-auto px-6 py-8 fade-in">
        <div className="tw-mono-label" data-testid="dashboard-brand-label">TIPPS ADAMS WEB-BASED TRAINING INITIATIVE · MY LEARNING</div>
        <h1 className="tw-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-2" data-testid="dashboard-welcome">Welcome back, {firstName}.</h1>
        <p className="text-stone-600 mt-2 max-w-2xl">{myCourses.length > 0 ? `You are enrolled in ${myCourses.length} course${myCourses.length>1?"s":""}. Keep going.` : "You have not enrolled in any courses yet."}</p>

        {active && (
          <section className="tw-card p-7 sm:p-9 mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="continue-banner">
            <div className="flex-1 min-w-0">
              <div className="tw-mono-label">CONTINUE LEARNING</div>
              <h2 className="tw-display text-2xl sm:text-3xl font-extrabold mt-2 leading-tight">{active.title}</h2>
              <div className="text-sm text-stone-500 mt-1">{active.course_id} · {active.completed_lessons}/{active.total_lessons} lessons · {active.progress_pct}%</div>
              <div className="tw-progress-track mt-4 max-w-md"><div className="tw-progress-fill" style={{width:`${active.progress_pct}%`, background:"var(--tawbti-blue-fill)"}}/></div>
            </div>
            <button onClick={()=>nav(`/courses/${active.course_id}`)} className="tw-btn-primary inline-flex items-center gap-2 self-start sm:self-auto" data-testid="resume-btn">
              Resume course <ArrowRight size={16}/>
            </button>
          </section>
        )}

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 stagger">
          <Stat label="Enrolled courses" value={myCourses.length} sub="Active enrollments" icon={BookOpen}/>
          <Stat label="Lessons completed" value={stats?.completed_lessons ?? 0} sub={`of ${stats?.total_lessons ?? 0} total`} icon={BarChart3}/>
          <Stat label="Quiz avg. score" value={`${stats?.avg_quiz_score ?? 0}%`} sub={(stats?.attempts_count||0)+" attempts"} icon={Trophy}/>
          <Stat label="Overall progress" value={`${stats?.overall_pct ?? 0}%`} sub="Across all courses" icon={Sparkles}/>
        </section>

        <div className="flex items-center justify-between mt-12 mb-4">
          <div className="tw-mono-label" data-testid="my-courses-label">MY COURSES</div>
          <Link to="/catalog" className="text-sm font-semibold inline-flex items-center gap-1 hover:underline" data-testid="browse-catalog-link"><Plus size={14}/> Browse catalog</Link>
        </div>

        {myCourses.length === 0 ? (
          <div className="tw-card p-10 text-center" data-testid="empty-courses">
            <p className="text-stone-600">No courses yet — head to the catalog to enroll.</p>
            <Link to="/catalog" className="tw-btn-primary inline-flex items-center gap-2 mt-4">Browse catalog <ArrowRight size={14}/></Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="my-courses-grid">
            {myCourses.map(c => <CourseCard key={c.course_id} c={c}/>)}
          </div>
        )}
      </div>
    </div>
  );
}
